import React from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { Link, useNavigate } from 'react-router-dom';
import useTranslation from '../../customHooks/translations';
import { DEFAULT_THEME_COLOR, FEED_YEARLY_COLOR } from '../../constants/theme';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import PeopleIcon from '@mui/icons-material/People';
import YouTubeIcon from '@mui/icons-material/YouTube';

const MEDALS = ['🏆', '🥈', '🥉'];

const medalBorderColor = (idx) => {
  if (idx === 0) return '#FFD70055';
  if (idx === 1) return '#C0C0C055';
  if (idx === 2) return '#CD7F3255';
  return `${DEFAULT_THEME_COLOR}44`;
};

const FeedListComponent = ({ data = [] }) => {
  const navigate = useNavigate();
  const translation = useTranslation();

  if (!data.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <LeaderboardIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.15)', mb: 2 }} />
        <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.95rem' }}>
          No feeds yet
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {data.map((item, idx) => (
        <Box
          key={item.id ?? idx}
          sx={{
            p: 2,
            borderRadius: 3,
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${medalBorderColor(idx)}`,
            transition: 'background 0.15s, border-color 0.15s',
            '&:hover': {
              background: 'rgba(255,255,255,0.06)',
            },
          }}
        >
          {/* Top row: rank + avatar + handle + user count */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Rank */}
            <Box sx={{ width: 28, textAlign: 'center', flexShrink: 0 }}>
              {idx < 3 ? (
                <Typography sx={{ fontSize: '1.3rem', lineHeight: 1 }}>{MEDALS[idx]}</Typography>
              ) : (
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', fontWeight: 700 }}>
                  #{idx + 1}
                </Typography>
              )}
            </Box>

            {/* Channel avatar */}
            <Avatar
              alt={item.forHandle}
              src={item.thumbnail || './icon-48x48.png'}
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                border: `2px solid ${medalBorderColor(idx)}`,
              }}
            />

            {/* Handle + user count */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.forHandle}
              </Typography>
              {item.userCount !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  <PeopleIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem' }}>
                    {item.userCount}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Description */}
          {item.description && (
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: '0.82rem',
                mt: 1.5,
                ml: '76px',
                lineHeight: 1.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {item.description}
            </Typography>
          )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, ml: '76px' }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate('/settings', { state: { youtube: item.forHandle } })}
              sx={{
                background: DEFAULT_THEME_COLOR,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.78rem',
                px: 2,
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { background: `${DEFAULT_THEME_COLOR}cc` },
              }}
            >
              Feed
            </Button>
            <Button
              size="small"
              variant="contained"
              component={Link}
              target="_blank"
              rel="noopener"
              to={`https://www.youtube.com/${item.forHandle}`}
              startIcon={<YouTubeIcon sx={{ fontSize: '1rem !important' }} />}
              sx={{
                background: FEED_YEARLY_COLOR,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.78rem',
                px: 2,
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { background: `${FEED_YEARLY_COLOR}cc` },
              }}
            >
              {translation.feeds.subscribe}
            </Button>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default FeedListComponent;
